import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  Tags,
  Ticket,
  Users,
  MessageSquare,
  Settings,
  Bell,
  Search,
  Moon,
  Sun,
  Menu,
  X,
  Store,
  Link as LinkIcon,
  FileText,
  ChevronDown,
  MessageCircle,
  Star,
  HelpCircle,
  ChevronRight,
  ShoppingBag,
  Sparkles,
  Shield,
  DollarSign,
  Layout,
  CreditCard,
  Truck,
  Share2,
  Cloud,
  Wand2,
  Activity,
  LogOut,
  UserCircle,
} from 'lucide-react';
import { LOGO_URL } from '../../constants';
import { useAuth } from '../contexts/AuthContext';
import { db, Quote } from '../services/backend';

const AdminLayout: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [recentQuotes, setRecentQuotes] = useState<Quote[]>([]);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);

  const location = useLocation();
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchQuotes = async () => {
      const quotes = await db.getQuotes();
      // Filter pending quotes and sort by date desc
      const pending = quotes
        .filter((q) => q.status === 'pending')
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 5);
      setRecentQuotes(pending);
    };
    fetchQuotes();

    // Poll every minute
    const interval = setInterval(fetchQuotes, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const toggleMenu = (label: string) => {
    setExpandedMenus((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]
    );
  };

  const menuItems = [
    // Dashboard
    {
      icon: <LayoutDashboard size={20} />,
      label: 'Dashboard',
      path: '/admin/dashboard',
      roles: ['master', 'admin', 'editor', 'viewer'],
    },

    // Aparência
    {
      icon: <Store size={20} />,
      label: 'Aparência',
      path: '/admin/store-layout',
      roles: ['master', 'admin'],
    },

    // Catálogo
    {
      icon: <Package size={20} />,
      label: 'Catálogo',
      path: '#',
      roles: ['master', 'admin', 'editor'],
      children: [
        {
          icon: <Package size={18} />,
          label: 'Produtos',
          path: '/admin/products',
          roles: ['master', 'admin', 'editor'],
        },
        {
          icon: <Package size={18} />,
          label: 'Kits',
          path: '/admin/kits',
          roles: ['master', 'admin', 'editor'],
        },
        {
          icon: <Tags size={18} />,
          label: 'Categorias',
          path: '/admin/categories',
          roles: ['master', 'admin', 'editor'],
        },
        {
          icon: <Ticket size={18} />,
          label: 'Cupons',
          path: '/admin/coupons',
          roles: ['master', 'admin'],
        },
      ],
    },

    // Interações
    {
      icon: <MessageCircle size={20} />,
      label: 'Interações',
      path: '#',
      roles: ['master', 'admin', 'editor'],
      children: [
        {
          icon: <Star size={18} />,
          label: 'Avaliações',
          path: '/admin/reviews',
          roles: ['master', 'admin', 'editor'],
        },
        {
          icon: <HelpCircle size={18} />,
          label: 'Perguntas',
          path: '/admin/questions',
          roles: ['master', 'admin', 'editor'],
        },
      ],
    },

    // Vendas & Clientes
    {
      icon: <ShoppingBag size={20} />,
      label: 'Vendas',
      path: '#',
      roles: ['master', 'admin', 'viewer'],
      children: [
        {
          icon: <Users size={18} />,
          label: 'Clientes',
          path: '/admin/customers',
          roles: ['master', 'admin', 'viewer'],
        },
        {
          icon: <DollarSign size={18} />,
          label: 'Pedidos',
          path: '/admin/sales',
          roles: ['master', 'admin', 'viewer'],
        },
        {
          icon: <Users size={18} />,
          label: 'Afiliados',
          path: '/admin/affiliates',
          roles: ['master', 'admin'],
        },
      ],
    },

    // Conteúdo
    {
      icon: <FileText size={20} />,
      label: 'Conteúdo',
      path: '#',
      roles: ['master', 'admin', 'editor'],
      children: [
        {
          icon: <FileText size={18} />,
          label: 'Blog',
          path: '/admin/blog',
          roles: ['master', 'admin', 'editor'],
        },
        {
          icon: <Layout size={18} />,
          label: 'Páginas (CMS)',
          path: '/admin/pages',
          roles: ['master', 'admin'],
        },
        {
          icon: <LinkIcon size={18} />,
          label: 'Menus',
          path: '/admin/menus',
          roles: ['master', 'admin'],
        },
      ],
    },

    // IA & Automação
    {
      icon: <Sparkles size={20} />,
      label: 'IA Generator',
      path: '/admin/ai-update',
      roles: ['master', 'admin'],
    },

    // Análise
    {
      icon: <Activity size={20} />,
      label: 'Performance',
      path: '/admin/performance',
      roles: ['master', 'admin'],
    },

    // Configurações
    {
      icon: <Settings size={20} />,
      label: 'Configurações',
      path: '#',
      roles: ['master', 'admin'],
      children: [
        {
          icon: <Settings size={18} />,
          label: 'Loja',
          path: '/admin/settings?tab=general',
          roles: ['master', 'admin'],
        },
        {
          icon: <CreditCard size={18} />,
          label: 'Pagamentos',
          path: '/admin/settings?tab=payments',
          roles: ['master', 'admin'],
        },
        {
          icon: <Truck size={18} />,
          label: 'Frete',
          path: '/admin/settings?tab=shipping',
          roles: ['master', 'admin'],
        },
        {
          icon: <Share2 size={18} />,
          label: 'Integrações',
          path: '/admin/settings?tab=integrations',
          roles: ['master', 'admin'],
        },
        {
          icon: <Cloud size={18} />,
          label: 'CDN',
          path: '/admin/cloudinary-migration',
          roles: ['master', 'admin'],
        },
        {
          icon: <Users size={18} />,
          label: 'Usuários',
          path: '/admin/users',
          roles: ['master', 'admin'],
        },
        {
          icon: <Shield size={18} />,
          label: 'Permissões',
          path: '/admin/permissions',
          roles: ['master'],
        },
      ],
    },
  ];

  const filteredMenuItems = menuItems
    .filter((item) => user && item.roles.includes(user.role))
    .map((item) => ({
      ...item,
      children: item.children
        ? item.children.filter((child) => user && child.roles.includes(user.role))
        : undefined,
    }))
    .filter((item) => !item.children || item.children.length > 0);

  return (
    <div
      className={`h-screen overflow-hidden flex ${isDarkMode ? 'dark bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}
    >
      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 transition-transform duration-300 ease-in-out ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0 lg:w-20'
        } ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-r flex flex-col`}
      >
        <div className="h-16 flex items-center justify-center border-b border-gray-200 dark:border-gray-700">
          <div className={`flex items-center ${!isSidebarOpen && 'lg:justify-center'}`}>
            {isSidebarOpen ? (
              <img src={LOGO_URL} alt="AlphaEstética" className="h-8 object-contain" />
            ) : (
              <img src={LOGO_URL} alt="A" className="h-8 w-8 object-cover lg:hidden" />
            )}
            <span className={`text-xs ml-2 text-gray-400 ${!isSidebarOpen && 'lg:hidden'}`}>
              Admin
            </span>
            {!isSidebarOpen && (
              <span className="hidden lg:block font-serif font-bold text-alphamed-gold text-xl">
                A
              </span>
            )}
          </div>
        </div>

        <nav className="flex-1 py-6 space-y-1 px-3 overflow-y-auto">
          {filteredMenuItems.map((item) => (
            <div key={item.label}>
              {item.children ? (
                <>
                  <button
                    onClick={() => toggleMenu(item.label)}
                    className={`w-full flex items-center justify-between px-3 py-3 rounded-md transition-colors group ${
                      expandedMenus.includes(item.label)
                        ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                        : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-400'
                    }`}
                  >
                    <div className="flex items-center">
                      <span className="shrink-0">{item.icon}</span>
                      <span
                        className={`ml-3 font-medium whitespace-nowrap transition-opacity duration-300 ${!isSidebarOpen ? 'lg:opacity-0 lg:w-0 lg:overflow-hidden' : 'opacity-100'}`}
                      >
                        {item.label}
                      </span>
                    </div>
                    {isSidebarOpen &&
                      (expandedMenus.includes(item.label) ? (
                        <ChevronDown size={16} />
                      ) : (
                        <ChevronRight size={16} />
                      ))}
                  </button>

                  {/* Submenu */}
                  {expandedMenus.includes(item.label) && isSidebarOpen && (
                    <div className="ml-4 mt-1 space-y-1 border-l-2 border-gray-200 dark:border-gray-700 pl-2">
                      {item.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          className={`flex items-center px-3 py-2 rounded-md transition-colors text-sm ${
                            location.pathname === child.path
                              ? 'text-alphamed-gold font-medium'
                              : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
                          }`}
                        >
                          <span className="shrink-0">{child.icon}</span>
                          <span className="ml-3">{child.label}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <Link
                  to={item.path}
                  className={`flex items-center px-3 py-3 rounded-md transition-colors group ${
                    location.pathname === item.path
                      ? 'bg-alphamed-gold/10 text-alphamed-gold'
                      : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-400'
                  }`}
                  title={item.label}
                >
                  <span className="shrink-0">{item.icon}</span>
                  <span
                    className={`ml-3 font-medium whitespace-nowrap transition-opacity duration-300 ${
                      !isSidebarOpen ? 'lg:opacity-0 lg:w-0 lg:overflow-hidden' : 'opacity-100'
                    }`}
                  >
                    {item.label}
                  </span>
                </Link>
              )}
            </div>
          ))}
        </nav>

        <div className="px-3 py-2">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center px-3 py-3 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-400 transition-colors group"
            title="Ir para Loja"
          >
            <Store size={20} className="shrink-0" />
            <span
              className={`ml-3 font-medium whitespace-nowrap transition-opacity duration-300 ${
                !isSidebarOpen ? 'lg:opacity-0 lg:w-0 lg:overflow-hidden' : 'opacity-100'
              }`}
            >
              Ir para Loja
            </span>
          </a>
        </div>

        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={handleLogout}
            className="flex items-center w-full text-gray-500 hover:text-red-500 transition-colors p-2 rounded-md hover:bg-red-50 dark:hover:bg-red-900/10"
            title="Sair do sistema"
          >
            <LogOut size={20} />
            <span className={`ml-3 font-medium ${!isSidebarOpen && 'lg:hidden'}`}>Sair</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header */}
        <header
          className={`h-16 flex items-center justify-between px-4 border-b ${
            isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}
        >
          <div className="flex items-center">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="p-2 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 lg:hidden"
            >
              <Menu size={20} />
            </button>
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="hidden lg:block p-2 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              {isSidebarOpen ? <Menu size={20} /> : <Menu size={20} />}
            </button>
          </div>

          <div className="flex items-center space-x-4">
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className="p-2 rounded-full text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>

            {/* Notifications */}
            <div className="relative">
              <button
                onClick={() => setNotificationsOpen(!notificationsOpen)}
                className="p-2 rounded-full text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 relative"
              >
                <Bell size={20} />
                {recentQuotes.length > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                )}
              </button>

              {notificationsOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50 border border-gray-200 dark:border-gray-700">
                  <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                      Notificações
                    </h3>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {recentQuotes.length > 0 ? (
                      recentQuotes.map((quote) => (
                        <Link
                          key={quote.id}
                          to={`/admin/quotes`}
                          onClick={() => setNotificationsOpen(false)}
                          className="block px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                          <div className="flex items-start">
                            <ShoppingBag className="h-5 w-5 text-alphamed-gold mt-0.5" />
                            <div className="ml-3">
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                Nova Cotação de {quote.customerName}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {new Date(quote.createdAt).toLocaleDateString()} às{' '}
                                {new Date(quote.createdAt).toLocaleTimeString()}
                              </p>
                            </div>
                          </div>
                        </Link>
                      ))
                    ) : (
                      <div className="px-4 py-6 text-center text-gray-500 dark:text-gray-400 text-sm">
                        Nenhuma nova notificação
                      </div>
                    )}
                  </div>
                  <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                    <Link
                      to="/admin/quotes"
                      onClick={() => setNotificationsOpen(false)}
                      className="block text-center text-xs font-medium text-alphamed-gold hover:text-alphamed-brown"
                    >
                      Ver todas as cotações
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <div className="relative">
              <button
                onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                className="flex items-center space-x-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full p-1 transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-alphamed-gold text-white flex items-center justify-center font-bold">
                  {user?.name?.charAt(0) || 'A'}
                </div>
                <span className="hidden md:block text-sm font-medium text-gray-700 dark:text-gray-200">
                  {user?.name || 'Admin'}
                </span>
                <ChevronDown size={14} className="text-gray-500" />
              </button>

              {isProfileMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50 border border-gray-200 dark:border-gray-700">
                  <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {user?.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {user?.email}
                    </p>
                  </div>
                  <a
                    href="/admin/settings?tab=general"
                    className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => setIsProfileMenuOpen(false)}
                  >
                    <UserCircle size={16} className="mr-2" />
                    Meu Perfil
                  </a>
                  <button
                    onClick={() => {
                      setIsProfileMenuOpen(false);
                      handleLogout();
                    }}
                    className="flex w-full items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/10"
                  >
                    <LogOut size={16} className="mr-2" />
                    Sair
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-4 md:p-8">
          <Outlet />
        </main>
      </div>

      {/* Overlay for mobile sidebar */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        ></div>
      )}
    </div>
  );
};

export default AdminLayout;
